<template>
  <base-stack class="user-transactions">
    <base-stack direction="row" justify="space-between" align="center">
      <p>
        <span>Balance: </span>
        <strong>{{ user.wallet.balance }}</strong>
        <span>({{ balanceUsd }})</span>
      </p>
      <base-button @click="onCreateManualCredit()">Add manual credit</base-button>
    </base-stack>
    <base-stack as="ul">
      <li
        v-for="transaction in user.wallet.transactions"
        :key="transaction.id"
        :id="transaction.id"
        :class="{ highlighted: highlightedId === transaction.id }"
      >
        <app-transaction-cell
          :transaction="transaction"
          :initial-collapsed="highlightedId !== transaction.id"
          @refund="onRefund"
        ></app-transaction-cell>
      </li>
    </base-stack>
  </base-stack>
</template>

<script lang="ts">
import AppTransactionCell from '@/components/TransactionCell.vue';
import {
  CreateManualCreditInput,
  Deposit,
  ITransaction,
  MeetAndGreetTicketPurchase,
  Order,
  RefundMeetAndGreetTicketInput,
  Ticket,
  User,
} from '@/types/api';
import gql from 'graphql-tag';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { isDeposit, isMeetAndGreetTicketPurchase, isOrder, isTicket } from '../utils/assessInterfaceType';

@Component({
  components: { AppTransactionCell },
})
export default class UserTransactions extends Vue {
  @Prop({ type: Object, required: true }) public user!: User;

  highlightedId: string | null = null;
  suggestedCreditRefund = 0;

  mounted() {
    this.handleHash();
  }

  @Watch('$route.hash')
  onHashChange() {
    this.handleHash();
  }

  handleHash() {
    const hash = this.$route.hash;
    if (hash && hash.startsWith('#')) {
      const id = hash.slice(1);
      this.highlightedId = id;
      this.$nextTick(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    } else {
      this.highlightedId = null;
    }
  }

  onRefund(transaction: ITransaction) {
    if (isDeposit(transaction)) this.refundDeposit(transaction);
    else if (isTicket(transaction)) this.refundTicket(transaction);
    else if (isOrder(transaction)) this.refundOrder(transaction);
    else if (isMeetAndGreetTicketPurchase(transaction)) this.refundMeetAndGreetTicketPurchase(transaction);
  }

  private toUsd(value: number) {
    return `$${value / 1000}`;
  }

  public get balanceUsd() {
    return this.toUsd(this.user.wallet.balance);
  }

  private async onCreateManualCredit() {
    const input = await this.$modal.prompt({
      title: 'Add manual credit',
      message: `Enter credits to add or subtract from the user wallet. Use a negative value to withdraw credits. (1000 credits = $1.00, current balance: ${this.toUsd(this.user.wallet.balance)})`,
      prompt: {
        label: 'Credits (1000 = $1.00)',
        placeholder: 'e.g. 1000 ($1.00) or -1000 (-$1.00)',
      },
    });
    if (!input) return;
    const credits = parseInt(input, 10);
    if (isNaN(credits) || credits === 0) {
      this.$modal.alert({ title: 'Invalid credits', message: 'Please enter a non-zero number of credits.' });
      return;
    }
    if (credits < 0 && this.user.wallet.balance + credits < 0) {
      this.$modal.alert({
        title: 'Invalid credits',
        message: 'This adjustment would reduce the user balance below 0.',
      });
      return;
    }
    await this.$apollo.mutate({
      mutation: gql`
        mutation CreateManualCredit($input: CreateManualCreditInput!) {
          createManualCredit(input: $input) {
            manualCredit {
              id
            }
          }
        }
      `,
      variables: { input: { userId: this.user.id, credits } satisfies CreateManualCreditInput },
      refetchQueries: ['User'],
      awaitRefetchQueries: true,
    });
  }

  private async refundDeposit(deposit: Deposit) {
    const maxRefund = Math.min(deposit.creditsRefundable, this.user.wallet.balance);
    const input = await this.$modal.prompt({
      title: 'Refund deposit',
      message: `Credits (max ${maxRefund} = ${this.toUsd(maxRefund)}) that will be withdrawn from user balance. A successful operation should create a stripe refund. User will also get an email notification.`,
      prompt: {
        label: 'Credits (1000 = $1.00)',
        placeholder: `${this.suggestedCreditRefund}`,
      },
    });
    if (!input) return;
    const credits = parseInt(input, 10);
    if (isNaN(credits) || credits < 1) {
      this.$modal.alert({ title: 'Invalid credits', message: 'Please enter a positive number of credits.' });
      return;
    }
    if (credits > maxRefund) {
      this.$modal.alert({ title: 'Invalid credits', message: 'This refund would reduce the user balance below 0.' });
      return;
    }
    await this.$apollo.mutate({
      mutation: gql`
        mutation CreateRefund($input: CreateRefundInput!) {
          createRefund(input: $input) {
            refund {
              id
            }
          }
        }
      `,
      variables: { input: { depositId: deposit.id, credits } },
      refetchQueries: ['User'],
      awaitRefetchQueries: true,
    });
    this.suggestedCreditRefund = 0;
  }

  private async refundTicket(ticket: Ticket) {
    const confirm = await this.$modal.confirm({
      title: 'Refund ticket',
      message: 'User will gain credits back. User will also get an email notification. Continue?',
    });
    if (!confirm) return;
    await this.$apollo.mutate({
      mutation: gql`
        mutation RefundTicket($input: RefundTicketInput!) {
          refundTicket(input: $input) {
            ticket {
              id
              refundedAt
            }
          }
        }
      `,
      variables: { input: { id: ticket.id } },
      refetchQueries: ['User'],
      awaitRefetchQueries: true,
    });
    this.suggestedCreditRefund += ticket.credits;
  }

  private async refundMeetAndGreetTicketPurchase(purchase: MeetAndGreetTicketPurchase) {
    const confirm = await this.$modal.prompt({
      title: 'Refund Secret Gathering',
      message: 'User will gain credits back. User will also get an email notification. Continue?',
      prompt: {
        label: 'How many?',
        placeholder: `${purchase.quantity}`,
      },
    });
    if (!confirm) return;
    const quantity = parseInt(confirm, 10);
    if (!quantity || quantity > purchase.quantity || quantity < 1) {
      this.$modal.alert({ title: 'Invalid quantity', message: 'Please enter a valid quantity' });
      return;
    }
    await this.$apollo.mutate({
      mutation: gql`
        mutation RefundMeetAndGreetTicket($input: RefundMeetAndGreetTicketInput!) {
          refundMeetAndGreetTicket(input: $input) {
            purchase {
              id
              refundedAt
            }
          }
        }
      `,
      variables: { input: { id: purchase.id, quantity } satisfies RefundMeetAndGreetTicketInput },
      refetchQueries: ['User'],
      awaitRefetchQueries: true,
    });
    this.suggestedCreditRefund += purchase.credits;
  }

  private async refundOrder(order: Order) {
    if (order.orderFulfillments.length > 0) {
      await this.$modal.alert({
        title: 'Cannot refund order',
        message:
          'This order has fulfillments (items were shipped or marked fulfilled). The system does not allow refunding it; the API rejects this case. Handle any reversal in Shopify and adjust the customer’s balance manually if needed.',
      });
      return;
    }
    const confirm = await this.$modal.confirm({
      title: 'Refund order',
      message:
        'User will gain credits back. User will also get an email notification. Make sure to cancel the Shopify order! Continue?',
    });
    if (!confirm) return;
    await this.$apollo.mutate({
      mutation: gql`
        mutation RefundOrder($input: RefundOrderInput!) {
          refundOrder(input: $input) {
            order {
              id
              refundedAt
            }
          }
        }
      `,
      variables: { input: { id: order.id } },
      refetchQueries: ['User'],
      awaitRefetchQueries: true,
    });
    this.suggestedCreditRefund += order.credits;
  }
}
</script>

<style lang="scss" scoped>
.user-transactions {
  padding: 1rem 0;
}

.box {
  margin: 16px 0;
  padding: 16px;
  border: 1px solid $color-theme-red;

  h3 {
    font-weight: bold;
  }
}

.highlighted {
  animation: highlight-fade 2s ease-out;
}

@keyframes highlight-fade {
  0% {
    background-color: rgba($color-theme-yellow, 0.3);
  }
  100% {
    background-color: transparent;
  }
}
</style>
